import React, { useState } from 'react';
import { Modal, Button, Table, Spinner } from 'react-bootstrap';
import { BsTrash } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import * as actions from '../../Redux/Actions/index';
import { showErrorToastMessage, showSuccessToastMessage } from '../Toasts/Toasts';
import ReassignmentModal from './ReassignmentModal'; // Reusing your existing modal

interface IClientManagerProps {
  show: boolean;
  onHide: () => void;
  data: { _id: string; clientName: string }[];
  refreshData: () => void;
  onReassignSuccess?: (oldId: string, newTarget: any) => void;
}

const ClientManagerModal = ({ show, onHide, data, refreshData, onReassignSuccess }: IClientManagerProps) => {
  const dispatch = useDispatch();
  
  // Delete & Confirm State
  const [itemToDelete, setItemToDelete] = useState<{id: string, name: string} | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reassign State
  const [reassignData, setReassignData] = useState<any>(null);

  const initiateDelete = (id: string, name: string) => {
    setItemToDelete({ id, name });
  };

  const confirmDelete = () => {
    if(!itemToDelete) return;
    setIsDeleting(true);

    // Pass null for transferTo initially
    dispatch(actions.deleteClientAction(itemToDelete.id, null, (res: any) => {
        setIsDeleting(false); // <--- Make sure this runs!

        // --- DEBUG LOGS ---
        console.log("🔵 CLIENT DELETE RESULT:", res);
        console.log("🔵 ERROR STATUS:", res?.error?.response?.status);
        console.log("🔵 META DATA:", res?.error?.response?.data?.meta_data);
        // ------------------

        // 1. Success
        if (res?.data || res?.code === 200) {
            showSuccessToastMessage("Client deleted successfully.");
            refreshData();
            setItemToDelete(null);
        }
        // 2. 412 Error (Employees Exist)
         else if (
            res?.code === 412 ||                        // <--- MATCHES YOUR LOG
            res?.error?.response?.status === 412 ||     // Matches Axios Error
            res?.error?.http_code === 412               // Matches ResultEntity
        ) {
            setItemToDelete(null); // Close Confirm Modal

            // EXTRACT DATA
            // Priority 1: Check flat 'meta_data' (Matches your log)
            // Priority 2: Check nested Axios structure
            const metaData = res.meta_data || res?.error?.response?.data?.meta_data;
            const count = Array.isArray(metaData) ? metaData.length : 0;

            console.log("🔵 OPENING REASSIGN WITH COUNT:", count);

            // Open Reassign Modal
            setReassignData({
                id: itemToDelete.id,
                name: itemToDelete.name,
                count: count
            });
        }
        // 3. Generic Error
        else {
            const errorMsg = res?.error?.response?.data?.error?.error_description || "Delete failed.";
            showErrorToastMessage(errorMsg);
            setItemToDelete(null);
        }
    }) as any);
  };

  return (
    <>
      {/* Main List Modal */}
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton><Modal.Title>Manage Clients</Modal.Title></Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {data && data.length > 0 ? (
              <Table hover responsive>
                  <tbody>
                      {data.map((item) => (
                          <tr key={item._id}>
                              <td className="align-middle">{item.clientName}</td>
                              <td className="text-end">
                                  <Button variant="outline-danger" size="sm" onClick={() => initiateDelete(item._id, item.clientName)}>
                                      <BsTrash />
                                  </Button>
                              </td>
                          </tr>
                      ))}
                  </tbody>
              </Table>
          ) : <p className="text-center text-muted">No clients found.</p>}
        </Modal.Body>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={!!itemToDelete} onHide={() => setItemToDelete(null)} centered size="sm">
        <Modal.Header closeButton><Modal.Title>Confirm Delete</Modal.Title></Modal.Header>
        <Modal.Body>Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?</Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setItemToDelete(null)} disabled={isDeleting}>No</Button>
            <Button variant="danger" onClick={confirmDelete} disabled={isDeleting}>
                {isDeleting ? <Spinner size="sm" animation="border" /> : "Yes, Delete"}
            </Button>
        </Modal.Footer>
      </Modal>

      {/* Reassignment Modal */}
      {reassignData && (
          <ReassignmentModal 
            show={!!reassignData}
            onHide={() => setReassignData(null)}
            conflictedItem={reassignData}
            type="CLIENT"
            customOptions={data.map(c => ({ value: c._id, label: c.clientName }))}
            onSuccess={(target: any) => {
                refreshData();
                if(onReassignSuccess) onReassignSuccess(reassignData.id, target);
                setReassignData(null);
            }}
           // customOptions={data.map(c => ({ value: c._id, label: c.clientName }))} 
          />
      )}
    </>
  );
};

export default ClientManagerModal;